/**
 * Cookie consent banner — GDPR/CCPA/ePrivacy notice rendered by
 * {@link ../../app.component.ts} on first visit and re-opened from the
 * footer "Cookie Preferences" entry-point.
 *
 * Visibility is derived from {@link CookieConsentService.hasDecided}:
 * the banner only renders when the user has not yet chosen for the
 * current policy version. Accept-all and reject-all delegate to the
 * service; "Customize" expands an inline preferences panel with a
 * toggle per non-essential category (functional / analytics /
 * marketing) — equal prominence is intentional, per EDPB guidance on
 * dark patterns.
 *
 * Regression-locked by {@link ./cookie-consent.component.spec.ts}.
 */
import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { CookieConsentService } from '../../services/cookie-consent.service';

@Component({
  selector: 'app-cookie-consent',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieConsentComponent {
  private readonly cookies = inject(CookieConsentService);

  /** Show the banner only until the user makes a decision. */
  readonly visible = computed(() => !this.cookies.hasDecided());

  /** True when the inline preferences panel is expanded. */
  readonly customizing = signal(false);

  /**
   * Working copy of the user's per-category selection while the
   * preferences panel is open. Committed to the service only when
   * the user clicks "Save preferences".
   */
  readonly draftFunctional = signal(false);
  readonly draftAnalytics = signal(false);
  readonly draftMarketing = signal(false);

  acceptAll(): void {
    this.cookies.acceptAll();
  }

  rejectAll(): void {
    this.cookies.rejectAll();
  }

  openCustomize(): void {
    const prefs = this.cookies.preferences();
    this.draftFunctional.set(prefs.functional);
    this.draftAnalytics.set(prefs.analytics);
    this.draftMarketing.set(prefs.marketing);
    this.customizing.set(true);
  }

  toggleFunctional(value: boolean): void {
    this.draftFunctional.set(value);
  }

  toggleAnalytics(value: boolean): void {
    this.draftAnalytics.set(value);
  }

  toggleMarketing(value: boolean): void {
    this.draftMarketing.set(value);
  }

  savePreferences(): void {
    this.cookies.savePreferences({
      functional: this.draftFunctional(),
      analytics: this.draftAnalytics(),
      marketing: this.draftMarketing(),
    });
    this.customizing.set(false);
  }
}
